import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  Search, Headphones, ClipboardList, FileText, Languages, Image as ImageIcon,
  FileSearch, FileScan, Brain, ShieldCheck, Cpu, Server,
  ChevronRight, X
} from 'lucide-react';
import { useState, useEffect } from 'react';

// ── icon maps ─────────────────────────────────────────────────────────────────

const ucIconMap: Record<string, React.ElementType> = {
  'knowledge-search':  Search,
  'customer-response': Headphones,
  'document-check':    FileScan,
  'meeting-followup':  ClipboardList,
  'proposal-reports':  FileText,
  'translate':         Languages,
  'doc-analysis':      FileSearch,
  'image-gen':         ImageIcon,
};

const moduleIconMap: Record<string, React.ElementType> = {
  'doc-understanding': FileText,
  'knowledge-memory':  Brain,
  'smart-search':      Search,
  'quality-check':     ShieldCheck,
  'ai-profiles':       Cpu,
};

const featureIconMap: Record<string, React.ElementType> = {
  'local-run':    Cpu,
  'roles-rights': ShieldCheck,
  'ki-profiles':  Brain,
  'costs':        FileText,
  'hosting':      Server,
};

// ── animation variants ────────────────────────────────────────────────────────

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] as [number, number, number, number] } }),
};

// ── sub-components ────────────────────────────────────────────────────────────

interface UseCaseTile {
  id: string;
  title: string;
  desc: string;
  soon?: boolean;
}

function UcTile({ tile, index }: { tile: UseCaseTile; index: number }) {
  const Icon = ucIconMap[tile.id] ?? Search;
  return (
    <motion.div
      variants={fadeUp}
      custom={index}
      className={`relative rounded-xl border p-4 ${
        tile.soon
          ? 'border-dashed border-amber-500/30 bg-amber-500/5'
          : 'border-border/60 bg-card/85 dark:bg-white/8'
      } backdrop-blur-sm`}
    >
      <div className="flex items-start gap-3">
        <span
          className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${
            tile.soon
              ? 'bg-amber-500/15 text-amber-400'
              : 'bg-primary/10 text-primary'
          }`}
        >
          <Icon className="h-4 w-4" />
        </span>
        <div className="min-w-0">
          <h5 className="flex flex-wrap items-center gap-2 text-sm font-semibold text-foreground leading-snug">
            {tile.title}
            {tile.soon && (
              <span className="rounded-full border border-amber-500/50 bg-amber-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-400">
                bald
              </span>
            )}
          </h5>
          <p className="mt-1 text-xs text-foreground/75 leading-relaxed">{tile.desc}</p>
        </div>
      </div>
    </motion.div>
  );
}

interface ModuleTile {
  id: string;
  title: string;
  desc: string;
}

function ModuleTile({ mod }: { mod: ModuleTile }) {
  const Icon = moduleIconMap[mod.id] ?? Brain;
  return (
    <div className="flex flex-col gap-2 rounded-xl border border-border/50 bg-card/80 dark:bg-white/7 p-4">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-primary shrink-0" />
        <h4 className="text-sm font-semibold text-foreground leading-snug">{mod.title}</h4>
      </div>
      <p className="text-xs text-foreground/70 leading-relaxed">{mod.desc}</p>
    </div>
  );
}

interface FeatureCard {
  id: string;
  color: string;
  title: string;
  bullets: string[];
}

function FeatCard({ card, index }: { card: FeatureCard; index: number }) {
  const Icon = featureIconMap[card.id] ?? Cpu;
  return (
    <motion.div
      variants={fadeUp}
      custom={index}
      className="flex flex-col gap-3 rounded-xl border border-border/60 bg-card/85 dark:bg-white/8 backdrop-blur-sm p-5"
    >
      <div className="flex items-center gap-2.5">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Icon className="h-4 w-4" />
        </span>
        <h3 className="text-base font-bold text-foreground leading-snug">{card.title}</h3>
      </div>
      <ul className="space-y-2">
        {card.bullets.map((b, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground leading-relaxed">
            <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary/60" />
            {b}
          </li>
        ))}
      </ul>
    </motion.div>
  );
}

// ── main component ─────────────────────────────────────────────────────────────

export function PlatformSection() {
  const { t } = useTranslation('home');
  const [ariaModalOpen, setAriaModalOpen] = useState(false);

  useEffect(() => {
    if (!ariaModalOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setAriaModalOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [ariaModalOpen]);

  const useCaseTiles: UseCaseTile[] = (
    t('platform.useCases', { returnObjects: true }) as UseCaseTile[]
  );
  const moduleTiles: ModuleTile[] = (
    t('platform.modules', { returnObjects: true }) as ModuleTile[]
  );
  const adminItems: string[] = (
    t('platform.admin', { returnObjects: true }) as string[]
  );
  const featureCards: FeatureCard[] = (
    t('platform.features', { returnObjects: true }) as FeatureCard[]
  );

  return (
    <section id="use-cases" className="relative py-20 overflow-hidden">
      {/* subtle scrim so NeuralBackground shows through softly */}
      <div className="absolute inset-0 bg-linear-to-b from-transparent via-background/50 to-transparent pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-20">

        {/* ── SECTION 1: Platform Economy (Slide 10) ─────────────────────── */}
        <div>
          {/* Header */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={fadeUp}
            className="text-center max-w-3xl mx-auto mb-12"
          >
            <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-primary mb-4">
              <span className="h-px w-8 bg-linear-to-r from-orange-500 to-primary rounded-full" />
              {t('platform.kicker')}
              <span className="h-px w-8 bg-linear-to-l from-orange-500 to-primary rounded-full" />
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4 leading-tight">
              {t('platform.title')}
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              {t('platform.description')}
            </p>
          </motion.div>

          {/* Use-case tiles grid */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-60px' }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4"
          >
            {useCaseTiles.map((tile, i) => (
              <UcTile key={tile.id} tile={tile} index={i} />
            ))}
          </motion.div>

          {/* Next-use-case bar */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="flex items-center justify-center gap-2 rounded-xl border border-dashed border-orange-500/40 bg-orange-500/5 px-5 py-3 text-sm font-semibold text-orange-400 mb-10"
          >
            <ChevronRight className="h-4 w-4 shrink-0" />
            {t('platform.nextUseCase')}
          </motion.div>

          {/* Connector lines */}
          <div className="hidden lg:flex justify-around px-12 h-6">
            {[0, 1, 2].map(i => (
              <div key={i} className="relative w-px h-full bg-linear-to-b from-primary/30 to-transparent">
                <motion.span
                  animate={{ top: ['0%', '100%'] }}
                  transition={{ duration: 1.8, repeat: Infinity, delay: i * 0.6, ease: 'linear' }}
                  className="absolute left-1/2 -translate-x-1/2 h-2 w-2 rounded-full bg-primary/70 shadow-[0_0_8px_rgba(99,102,241,0.6)]"
                />
              </div>
            ))}
          </div>

          {/* Platform modules */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-60px' }}
            className="mt-6"
          >
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-4">
              {t('platform.modulesLabel')}
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {moduleTiles.map(mod => <ModuleTile key={mod.id} mod={mod} />)}
            </div>
          </motion.div>

          {/* Admin row */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 rounded-xl border border-green-500/30 bg-green-500/5 px-5 py-3"
          >
            <span className="text-xs font-bold uppercase tracking-widest text-green-400 mr-1">
              {t('platform.adminLabel')}
            </span>
            {adminItems.map((item, i) => (
              <span key={i} className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <span className="h-1.5 w-1.5 rounded-full bg-green-400 shadow-[0_0_6px_rgba(52,211,153,0.6)]" />
                {item}
              </span>
            ))}
          </motion.div>
        </div>

        {/* ── SECTION 2: Enterprise Ready (Slide 11) ─────────────────────── */}
        <div>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={fadeUp}
            className="text-center max-w-3xl mx-auto mb-12"
          >
            <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-primary mb-4">
              <span className="h-px w-8 bg-linear-to-r from-orange-500 to-primary rounded-full" />
              {t('platform.enterpriseKicker')}
              <span className="h-px w-8 bg-linear-to-l from-orange-500 to-primary rounded-full" />
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4 leading-tight">
              {t('platform.enterpriseTitle')}
            </h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-60px' }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
          >
            {featureCards.map((card, i) => (
              <FeatCard key={card.id} card={card} index={i} />
            ))}
          </motion.div>
        </div>

        {/* ── SECTION 3: ARIA ─────────────────────────────────────────────── */}
        <div id="aria" className="scroll-mt-24">
          {/* ARIA header banner — kicker + title only */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={fadeUp}
            className="text-center max-w-3xl mx-auto mb-12"
          >
            <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-purple-500 dark:text-purple-400 mb-4">
              <span className="h-px w-8 bg-linear-to-r from-purple-400 to-purple-600 rounded-full" />
              {t('ariaResults.kicker')}
              <span className="h-px w-8 bg-linear-to-l from-purple-400 to-purple-600 rounded-full" />
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground leading-tight text-balance">
              {t('ariaResults.title')}
            </h2>
          </motion.div>

          {/* ARIA spotlight card — purple themed */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.25 }}
            className="rounded-2xl border border-purple-500/30 bg-linear-to-br from-purple-500/10 via-indigo-500/5 to-transparent backdrop-blur-sm p-7 lg:p-9"
          >
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10 items-center">
              {/* Col 1 — logo + wordmark */}
              <div className="flex flex-col items-center gap-4">
                <motion.div
                  initial={{ boxShadow: '0 0 40px rgba(139,92,246,0.7)' }}
                  whileHover={{ scale: 1.04, y: -6, boxShadow: '0 0 80px rgba(168,85,247,1)' }}
                  transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                  onClick={() => setAriaModalOpen(true)}
                  className="relative rounded-3xl overflow-hidden cursor-pointer ring-1 ring-purple-500/30"
                >
                  <video
                    src="/videos/aria-turntable.mp4"
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="h-40 sm:h-48 lg:h-52 w-auto object-contain block"
                  />
                </motion.div>
                <span className="text-5xl sm:text-6xl font-black tracking-widest uppercase bg-gradient-to-br from-blue-300 via-purple-400 to-purple-500 bg-clip-text text-transparent drop-shadow-[0_0_24px_rgba(168,85,247,0.4)]">
                  ARIA
                </span>
                <p className="text-xs text-muted-foreground/60 italic text-center">
                  {t('platform.aria.footnote')}
                </p>
              </div>

              {/* Col 2 — hook + description */}
              <div className="flex flex-col gap-3">
                <p className="text-lg sm:text-xl font-semibold text-foreground leading-snug">
                  {t('platform.aria.hook')}
                </p>
                <p className="text-base text-muted-foreground leading-relaxed">
                  {t('platform.aria.description')}
                </p>
              </div>

              {/* Col 3 — benefit chips stacked */}
              <div className="flex flex-col gap-4">
                {(t('platform.aria.benefits', { returnObjects: true }) as string[]).map((b, i) => (
                  <div key={i} className="flex items-center gap-3 rounded-xl border border-purple-500/25 bg-purple-500/8 px-5 py-5">
                    <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-purple-400 shadow-[0_0_8px_rgba(168,85,247,0.7)]" />
                    <span className="text-base font-semibold text-foreground/90">{b}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Subtitle below the spotlight card */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-60px' }}
            variants={fadeUp}
            className="text-center max-w-3xl mx-auto mt-8"
          >
            <p className="text-lg text-muted-foreground leading-relaxed">
              {t('ariaResults.subtitle')}
            </p>
          </motion.div>
        </div>

      </div>
      {/* ARIA Video Modal */}
      <AnimatePresence>
        {ariaModalOpen && (
          <motion.div
            key="aria-modal-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            onClick={() => setAriaModalOpen(false)}
          >
            <motion.div
              key="aria-modal-content"
              initial={{ scale: 0.85, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.85, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 280, damping: 24 }}
              onClick={(e) => e.stopPropagation()}
              className="relative rounded-3xl overflow-hidden shadow-[0_0_80px_rgba(168,85,247,0.8)] ring-2 ring-purple-500/50 max-w-3xl w-full"
            >
              <video
                src="/videos/aria-turntable.mp4"
                autoPlay
                loop
                muted
                playsInline
                className="w-full h-auto block"
              />
              <button
                onClick={() => setAriaModalOpen(false)}
                className="absolute top-3 right-3 flex items-center justify-center w-9 h-9 rounded-full bg-black/60 hover:bg-black/80 text-white transition-colors"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
